/**
 * A project's contacts (design 3.8): who from the address book works on it, and in what role. One
 * project can have many (an electrician, an architect, a lawyer); one contact can hold several roles.
 */
import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Container, ProjectContact } from "@workcore/schemas";
import { api, checkedWrite, get } from "../../api";
import { Breadcrumbs, EmptyState, ErrorBox, PageHeader, Panel, RowMenu, toast } from "../../ui";
import { confirmAction } from "../../confirm";
import { InlineText } from "../editor/shared";
import { useContactSearch, useContactSettings } from "../contacts/Contacts";
import { useMe } from "./Projects";

export function ProjectContactsPage() {
  const { slug = "", id: idParam = "" } = useParams();
  const id = Number(idParam);
  const q = useQueryClient();
  const me = useMe(slug).data;
  const container = useQuery({ queryKey: ["ws", slug, "container", id, "detail"], queryFn: async () => Container.parse(await get(`/v1/ws/${slug}/containers/${id}`)) });
  const links = useQuery({ queryKey: ["ws", slug, "container", id, "contacts"], queryFn: async () => z.array(ProjectContact).parse(await get(`/v1/ws/${slug}/containers/${id}/contacts`)) });
  const c = container.data;
  const refresh = () => q.invalidateQueries({ queryKey: ["ws", slug, "container", id, "contacts"] });
  const write = useMutation({
    mutationFn: ({ method, path, body }: { method: string; path: string; body?: unknown }) => checkedWrite(method, path, body),
    onSuccess: () => toast("Saved"),
    onSettled: refresh,
  });
  if (!c) return <ErrorBox error={container.error} />;
  const canEdit = !!me && (me.isSystemAdmin || (c.isTemplate ? me.roles.some((r) => r.role === "workflow_admin" && r.scopeId === c.definitionId) : me.roles.some((r) => r.role === "project_admin" && r.scopeId === id)));
  const rows = links.data ?? [];
  const base = `/v1/ws/${slug}/containers/${id}/contacts`;
  return (
    <>
      <PageHeader
        back={<Breadcrumbs items={[{ label: "Projects", to: `/w/${slug}/projects` }, { label: c.name }]} />}
        title={`${c.name}: contacts`}
        description={c.isTemplate ? "Firms and people every project made from this template starts with." : "Who from the address book works on this project, and in what role. Work items can name them in contact fields."}
      />
      <ErrorBox error={links.error ?? write.error} />
      {links.isSuccess && rows.length === 0 ? (
        <EmptyState title="No contacts on this project" body="Add the electrician, the architect, the lawyer: anyone from the address book, each in the role they play here." />
      ) : (
        <Panel plain>
          <table className="grid">
            <thead><tr><th>Role</th><th>Name</th><th>Email</th><th className="opt">Phone</th><th className="opt">Notes</th><th /></tr></thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id}>
                  <td><InlineText label={`${l.contact.name} role`} value={l.role} disabled={!canEdit} onSave={(v) => write.mutate({ method: "PATCH", path: `${base}/${l.id}`, body: { role: v } })} /></td>
                  <td>{l.contact.name}{l.contact.kind === "organisation" && <span className="muted small"> firm</span>}</td>
                  <td>{l.contact.email ?? ""}</td>
                  <td className="opt">{l.contact.phone ?? ""}</td>
                  <td className="opt"><InlineText label={`${l.contact.name} notes`} value={l.notes} disabled={!canEdit} onSave={(v) => write.mutate({ method: "PATCH", path: `${base}/${l.id}`, body: { notes: v } })} /></td>
                  <td className="row-actions">
                    {canEdit && (
                      <RowMenu
                        label={`Actions for ${l.contact.name} as ${l.role}`}
                        items={[
                          {
                            label: "Take off the project…",
                            danger: true,
                            onSelect: () =>
                              void confirmAction({ title: `Take ${l.contact.name} off as ${l.role}?`, body: "They stay in the address book. Undo puts them back.", action: "Take off", check: () => api("DELETE", `${base}/${l.id}`, { dryRun: true }) }).then((ok) => ok && write.mutate({ method: "DELETE", path: `${base}/${l.id}` })),
                          },
                        ]}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
      {canEdit && <AddContact slug={slug} onAdd={(body) => write.mutate({ method: "POST", path: base, body })} />}
    </>
  );
}

function AddContact({ slug, onAdd }: { slug: string; onAdd: (body: { contactId: number; role: string; notes: string }) => void }) {
  const [text, setText] = useState("");
  const [contactId, setContactId] = useState("");
  const [role, setRole] = useState("");
  const found = useContactSearch(slug, text).data?.items ?? [];
  const { categories } = useContactSettings(slug);
  const chosen = found.find((c) => c.id === Number(contactId));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onAdd({ contactId: Number(contactId), role, notes: "" });
    setRole("");
    setContactId("");
  };
  return (
    <Panel title="Add a contact">
      <form autoComplete="off" className="inline wrap" onSubmit={submit}>
        <label className="inline-field"><span>Find</span><input aria-label="Find contact" placeholder="name or email" value={text} onChange={(e) => setText(e.target.value)} /></label>
        <label className="inline-field"><span>Contact</span><select aria-label="Contact to add" required value={contactId} onChange={(e) => {
          setContactId(e.target.value);
          const c = found.find((x) => x.id === Number(e.target.value));
          // suggest the role from the contact's first category
          if (c && !role && c.categories[0]) setRole(categories.find((k) => k.key === c.categories[0])?.label ?? "");
        }}>
          <option value="">choose…</option>
          {found.map((c) => <option key={c.id} value={c.id}>{c.name}{c.email ? ` (${c.email})` : ""}</option>)}
        </select></label>
        <label className="inline-field"><span>Role on this project</span><input aria-label="Role on this project" required list="contact-roles" placeholder="e.g. Electrician" value={role} onChange={(e) => setRole(e.target.value)} /></label>
        <datalist id="contact-roles">{categories.filter((c) => !c.archived).map((c) => <option key={c.key} value={c.label} />)}</datalist>
        <button type="submit" disabled={!chosen}>Add to project</button>
      </form>
      <p className="muted small">Someone not in the address book yet? Add them on the Contacts page first.</p>
    </Panel>
  );
}
